import { useCallback } from 'react'
import type { LocationInfoData } from 'tripPlanner/components/Chat/Message'
import type { OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'
import { useDrawerSlot, type DrawerSlot } from './useDrawerSlot'

export interface PlaceDetails {
  readonly googlePlaceId: string
  readonly placeName: string
  readonly countryName?: string
  readonly stopId?: number
}

export interface InlinePlace {
  readonly googlePlaceId: string
  readonly placeName: string
}

export interface OfferDrawerData {
  readonly offerId: string
  readonly accommodationPropertyId?: string
  readonly searchParams?: OfferClickSearchParams
  readonly offerType?: string
  readonly scrollToPricing?: boolean
  readonly scrollToRoomOptions?: boolean
}

export interface UseDrawerStateReturn {
  detailPanel: DrawerSlot<PlaceDetails>
  placeInfo: DrawerSlot<InlinePlace>
  locationInfo: DrawerSlot<LocationInfoData>
  offerDrawer: DrawerSlot<OfferDrawerData>
  activeDrawerId: DrawerId | undefined
  closeActiveDrawer: () => void
  resetDrawers: () => void
}

export type DrawerId = 'detailPanel' | 'placeInfo' | 'locationInfo' | 'offerDrawer'

function getActiveDrawerId({
  detailPanel,
  locationInfo,
  offerDrawer,
  placeInfo,
}: Pick<UseDrawerStateReturn, 'detailPanel' | 'locationInfo' | 'offerDrawer' | 'placeInfo'>): DrawerId | undefined {
  if (offerDrawer.isOpen) return 'offerDrawer'
  if (locationInfo.isOpen) return 'locationInfo'
  if (placeInfo.isOpen) return 'placeInfo'
  if (detailPanel.isOpen) return 'detailPanel'
  return undefined
}

export function useDrawerState(): UseDrawerStateReturn {
  const detailPanel = useDrawerSlot<PlaceDetails>()
  const placeInfo = useDrawerSlot<InlinePlace>()
  const locationInfo = useDrawerSlot<LocationInfoData>()
  const offerDrawer = useDrawerSlot<OfferDrawerData>()
  const activeDrawerId = getActiveDrawerId({
    detailPanel,
    locationInfo,
    offerDrawer,
    placeInfo,
  })

  const resetDrawers = useCallback(() => {
    detailPanel.reset()
    placeInfo.reset()
    locationInfo.reset()
    offerDrawer.reset()
  }, [detailPanel, placeInfo, locationInfo, offerDrawer])

  const closeActiveDrawer = useCallback(() => {
    switch (activeDrawerId) {
      case 'detailPanel':
        detailPanel.close()
        return
      case 'placeInfo':
        placeInfo.close()
        return
      case 'locationInfo':
        locationInfo.close()
        return
      case 'offerDrawer':
        offerDrawer.close()
        return
    }
  }, [activeDrawerId, detailPanel, locationInfo, offerDrawer, placeInfo])

  return {
    detailPanel,
    placeInfo,
    locationInfo,
    offerDrawer,
    activeDrawerId,
    closeActiveDrawer,
    resetDrawers,
  }
}
